<script setup lang="ts">
import { USER_ROLES } from '~/constants/auth';
import { validatePhone } from '~/utils/phone/validatePhone';

// Required WhatsApp-number prompt for signed-in agents / masterclass leads whose
// account has no usable phone (older email-only and Google signups). The team
// follows up on WhatsApp, so the portal stays blocked until a valid number is
// saved. Hidden while an admin/AM is "viewing as" the agent (read-only).
const { t } = useI18n();
const { toErrorMessage } = useErrorMessage();
const authStore = useAuthStore();
const { mutateAsync: updatePhone, isPending } = useUpdatePhone();

const phone = ref<string | null>(null);
const phoneValid = ref(false);
const submitError = ref('');

// Only decide once /api/user/session (refetched by app.vue on load and merged
// into the store) has confirmed the DB state for THIS user — a persisted store
// user from an older login payload may simply lack the phone field.
const { data: sessionUser, refetch: refetchSession } = useCurrentUser(false);

// SPA logins (no full reload) never re-run app.vue's session fetch — pull it
// once for the newly signed-in user.
watch(
  () => authStore.user?.id,
  (id) => {
    if (id && authStore.token && sessionUser.value?.id !== id) refetchSession();
  },
  { immediate: true },
);

const needsPhone = computed(() => {
  const user = authStore.user;
  if (!user || authStore.getIsImpersonating) return false;
  if (sessionUser.value?.id !== user.id) return false;
  if (user.role !== USER_ROLES.AGENT && user.role !== USER_ROLES.LEAD) return false;
  return !validatePhone(user.phone).valid;
});

const hasStoredPhone = computed(() => !!authStore.user?.phone);

const onSubmit = async () => {
  if (!phone.value || !phoneValid.value) return;
  submitError.value = '';
  try {
    await updatePhone(phone.value);
  } catch (error: unknown) {
    // useErrorHandler (mutation onError) reports it; show the reason inline too.
    const e = error as { data?: { statusMessage?: string } };
    submitError.value = toErrorMessage(error, e?.data?.statusMessage || t('shell.phoneCaptureModal.submitError'));
  }
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="needsPhone"
      class="fixed inset-0 z-[110] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="phone-capture-title"
    >
      <div class="absolute inset-0 bg-black/40 backdrop-blur-md" />
      <form
        class="relative flex w-full max-w-md flex-col gap-4 rounded-2xl bg-white p-6 shadow-2xl"
        @submit.prevent="onSubmit"
      >
        <div>
          <h2
            id="phone-capture-title"
            class="font-serif text-[22px] font-semibold tracking-[-0.015em] text-black-100"
          >
            {{ hasStoredPhone ? t('shell.phoneCaptureModal.confirmTitle') : t('shell.phoneCaptureModal.addTitle') }}
          </h2>
          <p class="mt-1 text-sm text-black-60">
            {{
              hasStoredPhone
                ? t('shell.phoneCaptureModal.confirmDescription')
                : t('shell.phoneCaptureModal.addDescription')
            }}
          </p>
        </div>
        <CommonPhoneInput
          id="phone-capture-input"
          v-model="phone"
          v-model:valid="phoneValid"
          required
          :label="t('shell.phoneCaptureModal.phoneLabel')"
        />
        <p
          v-if="submitError"
          class="text-sm text-red-500"
        >
          {{ submitError }}
        </p>
        <UIButton
          id="phone-capture-save"
          type="submit"
          full-width
          class="!h-[48px] !rounded-xl"
          :loading="isPending"
          :disabled="!phone || !phoneValid"
        >
          {{ t('shell.phoneCaptureModal.saveButton') }}
        </UIButton>
      </form>
    </div>
  </Teleport>
</template>
